import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Seo from "../components/Seo";
import { SITE_ORIGIN } from "../seo/openGraph";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  Send,
  Timer,
  BarChart3,
  Lock,
  Lightbulb,
  Sparkles,
  Code,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  LEAD_CARD_CLASS,
  LEAD_FIELD_CLASS,
  LEAD_SUBCOPY_CLASS,
  LEAD_TEXTAREA_CLASS,
  LEAD_TRUST_CLASS,
} from "../components/leadMagnetStyles";
import BreadcrumbSchema from "../components/BreadcrumbSchema";
import IdeaValidatorModal from "../components/IdeaValidator/IdeaValidatorModal";
import AppCostEstimatorModal from "../components/AppCostEstimator/AppCostEstimatorModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  getRelatedLandingPages,
  landingCategoryMeta,
} from "../data/landingPages";
import { getLandingShowcase } from "../data/landingShowcases";
import {
  CASE_STUDY_TEASERS,
  resolveLandingShowcase,
  resolveLandingVoice,
  sanitizeLandingCopy,
} from "../data/landingVoice";
import {
  mobileAppService,
  iosMobileService,
  androidMobileService,
  hybridMobileService,
  lowCodeMobileService,
  webAppService,
  customSoftwareService,
  ecommerceService,
  aiMlService,
} from "../data/services";
import { trackLead } from "@/lib/analytics";
import {
  HoneypotField,
  LEAD_SUBMIT_ERROR,
  LeadFormError,
  honeypotValue,
  submitAppsContact,
  useLeadFormGuard,
} from "@/lib/hubspot";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

const allServices = [
  mobileAppService,
  iosMobileService,
  androidMobileService,
  hybridMobileService,
  lowCodeMobileService,
  webAppService,
  customSoftwareService,
  ecommerceService,
  aiMlService,
];

function resolveServiceByPath(path) {
  return allServices.find((s) => s.path === path) || null;
}

function useFaqSchema(faqs, path) {
  useEffect(() => {
    if (!faqs || faqs.length === 0) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-faq-schema", "true");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [faqs, path]);
}

const HUB_BROWSE = {
  service: { label: "App Development Services", tab: "services" },
  industry: { label: "Industry Solutions", tab: "industries" },
  location: { label: "Local App Development", tab: "regions" },
  campaign: { label: "Tools & Resources", tab: "tools" },
};

function hubUrlForCategory(category) {
  const hub = HUB_BROWSE[category];
  if (!hub) return `${SITE_ORIGIN}/#browse-solutions`;
  return `${SITE_ORIGIN}/?browseHub=${hub.tab}#browse-solutions`;
}

function useServiceSchema(page) {
  useEffect(() => {
    const areaServed = page.location
      ? {
          "@type": "City",
          name: page.location.region
            ? `${page.location.city}, ${page.location.region}`
            : page.location.city,
          containedInPlace: {
            "@type": "Country",
            name: page.location.country === "US" ? "United States" : page.location.country,
          },
        }
      : { "@type": "Country", name: "United States" };

    const schema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${SITE_ORIGIN}${page.path}#service`,
      serviceType: page.shortTitle || page.title,
      name: page.title,
      description: sanitizeLandingCopy(page.metaDescription),
      provider: {
        "@type": "Organization",
        "@id": `${SITE_ORIGIN}/#organization`,
        name: "Maxiom Apps",
        url: SITE_ORIGIN,
        logo: `${SITE_ORIGIN}/maxiom-apps-logo.png`,
      },
      areaServed,
      url: `${SITE_ORIGIN}${page.path}`,
      offers: {
        "@type": "Offer",
        availability: "https://schema.org/InStock",
        priceCurrency: "USD",
        url: `${SITE_ORIGIN}${page.path}#lead-form`,
      },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-service-schema", "true");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [page]);
}

function useLocalBusinessSchema(page) {
  useEffect(() => {
    if (page.category !== "location" || !page.location) return;
    const placeName = page.location.region
      ? `${page.location.city}, ${page.location.region}`
      : page.location.city;
    const schema = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "@id": `${SITE_ORIGIN}${page.path}#localbusiness`,
      name: `Maxiom Apps (${placeName})`,
      description: sanitizeLandingCopy(page.metaDescription),
      url: `${SITE_ORIGIN}${page.path}`,
      image: `${SITE_ORIGIN}/maxiom-apps-logo.png`,
      telephone: "+1-703-942-9420",
      priceRange: "$$$",
      areaServed: {
        "@type": "City",
        name: placeName,
      },
      address: {
        "@type": "PostalAddress",
        streetAddress: "44927 George Washington Blvd Suite 265",
        addressLocality: "Ashburn",
        addressRegion: "VA",
        postalCode: "20147",
        addressCountry: "US",
      },
      parentOrganization: {
        "@type": "Organization",
        "@id": `${SITE_ORIGIN}/#organization`,
        name: "Maxiom Apps",
        url: SITE_ORIGIN,
      },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-localbusiness-schema", "true");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [page]);
}

function LandingLeadForm({ page, voice, onFirstNameChange }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { companyUrl, setCompanyUrl, mountedAt } = useLeadFormGuard();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });

  const formName = `Landing - ${page.title}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const outcome = await submitAppsContact(
        {
          email: form.email,
          name: form.name,
          company: form.company,
          phone: form.phone,
          message: form.message,
          source: "landing_lead_form",
          landingTitle: page.title,
          landingSlug: page.slug,
        },
        {
          companyUrl: honeypotValue(e, companyUrl),
          mountedAt,
          name: form.name,
        },
      );
      if (outcome === "sent") {
        trackLead("landing_lead_form", { landing_title: page.title });
      }
      setSubmitted(true);
    } catch {
      setError(LEAD_SUBMIT_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNameChange = (value) => {
    setForm({ ...form, name: value });
    onFirstNameChange?.(value.trim());
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`${LEAD_CARD_CLASS} rounded-xl p-10 text-center`}
        data-testid="landing-lead-thank-you"
      >
        <CheckCircle className="w-12 h-12 text-[#00AEEF] mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-white mb-3">Thank You!</h3>
        <p className="text-gray-200 text-sm">
          A senior engineer will read this and reply within one business day.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      onSubmit={handleSubmit}
      name={formName}
      data-form-name={formName}
      data-page-slug={page.slug}
      data-page-type={page.pageType}
      data-hs-do-not-collect="true"
      aria-busy={submitting}
      className={`${LEAD_CARD_CLASS} rounded-2xl p-7 md:p-8 space-y-4`}
      data-testid={`landing-lead-form-${page.slug}`}
    >
      <HoneypotField value={companyUrl} onChange={setCompanyUrl} />
      <div>
        <h3 className="text-xl font-semibold text-white mb-1">
          {voice.leadFormHeadline}
        </h3>
        <p className={LEAD_SUBCOPY_CLASS}>
          {voice.leadFormSubheadline}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          name="name"
          required
          placeholder="Full Name *"
          value={form.name}
          onChange={(e) => handleNameChange(e.target.value)}
          className={LEAD_FIELD_CLASS}
          data-testid={`landing-input-name-${page.slug}`}
        />
        <Input
          name="email"
          type="email"
          required
          placeholder="Work Email *"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className={LEAD_FIELD_CLASS}
          data-testid={`landing-input-email-${page.slug}`}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          name="company"
          placeholder="Company"
          value={form.company}
          onChange={(e) => setForm({ ...form, company: e.target.value })}
          className={LEAD_FIELD_CLASS}
        />
        <Input
          name="phone"
          type="tel"
          placeholder="Phone (optional)"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className={LEAD_FIELD_CLASS}
        />
      </div>

      <textarea
        name="message"
        placeholder="Tell us about your project (optional)"
        rows={3}
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        className={LEAD_TEXTAREA_CLASS}
      />

      {/* Landing context is also copied into the HubSpot message. */}
      <input
        className="sr-only"
        type="text"
        name="landing_page_slug"
        value={page.slug}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
      />
      <input
        className="sr-only"
        type="text"
        name="landing_page_type"
        value={page.pageType}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
      />
      <input
        className="sr-only"
        type="text"
        name="landing_page_category"
        value={page.category}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
      />
      <input
        className="sr-only"
        type="text"
        name="landing_target_keyword"
        value={page.targetKeyword || ""}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
      />
      <input
        className="sr-only"
        type="text"
        name="landing_source_path"
        value={`${SITE_ORIGIN}${page.path}`}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
      />
      <input
        className="sr-only"
        type="text"
        name="landing_cta_label"
        value={voice.primaryCtaLabel || ""}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
      />

      <LeadFormError message={error} testId={`landing-lead-error-${page.slug}`} />
      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#00AEEF] hover:bg-[#0095CC] text-white font-semibold py-5 text-sm rounded-md transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]"
        data-testid={`landing-submit-${page.slug}`}
      >
        {submitting ? "Sending..." : voice.primaryCtaLabel || "Schedule a Call"}{" "}
        {!submitting && <Send className="ml-2 w-4 h-4" />}
      </Button>
      <p className={LEAD_TRUST_CLASS}>
        By submitting, you agree to receive occasional updates from Maxiom
        Apps. No spam, ever.
      </p>
    </motion.form>
  );
}

function TrustBullets({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="space-y-2.5 mb-8">
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-center gap-2.5 text-[14px] text-gray-300"
        >
          <CheckCircle className="w-4 h-4 text-[#00AEEF] flex-shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function TrustBar() {
  return (
    <section
      className="py-10 bg-[#0A0A0A] border-y border-white/5"
      data-testid="landing-trust-bar"
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <p className="text-center text-xs uppercase tracking-widest text-neutral-400 mb-3">
          Work that already exists
        </p>
        <p className="text-center text-sm text-gray-400 mb-6 max-w-2xl mx-auto">
          App studio since 2002. $100M+ contract value. 221+ clients. Case
          studies live on maxiomtech.com. We don&apos;t invent metrics here.
        </p>
        <div className="flex flex-col items-center gap-5">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {CASE_STUDY_TEASERS.map((cs) => (
              <a
                key={cs.url}
                href={cs.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white hover:text-[#00AEEF] transition-colors underline-offset-4 hover:underline"
              >
                {cs.name}
              </a>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16 opacity-50 hover:opacity-70 transition-opacity">
            {[
              {
                name: "Google",
                url: "https://www.maxiomtech.com/images/logos/logo-3-black.svg",
              },
              {
                name: "BMW",
                url: "https://www.maxiomtech.com/images/logos/Mask-Group-6.png",
              },
              {
                name: "Exelis",
                url: "https://www.maxiomtech.com/images/logos/exelis-3-LOGO.png",
              },
              {
                name: "Telus",
                url: "https://www.maxiomtech.com/images/logos/Mask-Group-5.png",
              },
            ].map((logo) => (
              <img
                key={logo.name}
                src={logo.url}
                alt={`${logo.name} logo`}
                className="h-7 md:h-9 w-auto brightness-0 invert object-contain"
                loading="lazy"
              />
            ))}
          </div>
          <a
            href="https://clutch.co/profile/maxiom-technology"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-gray-200 hover:border-[#00AEEF]/60 hover:bg-[#00AEEF]/10 transition-colors"
            data-testid="landing-clutch-badge"
          >
            <span className="flex h-5 w-20 items-center justify-center rounded bg-transparent text-[11px] font-bold text-[#F59E0B] tracking-tight leading-none">
              ★★★★★
            </span>
            <span className="font-semibold text-white">Rated on Clutch</span>
            <span className="text-gray-400 hidden sm:inline">
              Maxiom Technology profile
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

function IdeaValidatorTeaser({ onOpen }) {
  return (
    <section
      data-testid="landing-idea-validator-teaser"
      className="py-16 md:py-20 bg-[#0A0A0A] border-y border-white/5"
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="relative overflow-hidden rounded-2xl border border-white/5 bg-[#121212] p-8 md:p-10">
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#00AEEF] to-[#06B6D4]" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#00AEEF]/10 via-transparent to-transparent pointer-events-none" />
          <div className="relative flex flex-col lg:flex-row lg:items-center gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-[#00AEEF]/10 border border-[#00AEEF]/30 rounded-full px-4 py-[6px] text-[12px] font-medium text-[#06B6D4] tracking-[0.5px] mb-5">
                Free App Idea Assessment
              </div>
              <h2 className="font-bold text-[clamp(24px,4vw,36px)] leading-[1.15] mb-4 text-white">
                How Good is Your{" "}
                <span className="bg-gradient-to-r from-[#00AEEF] to-[#06B6D4] bg-clip-text text-transparent">
                  App Idea?
                </span>
              </h2>
              <p className="text-neutral-400 text-[15px] leading-relaxed mb-6">
                Eight questions. A senior engineer reads the result. You get a
                viability read and a next step, not a drip sequence. We&apos;ve
                been shipping since 2002.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 flex-wrap mb-6">
                <div className="flex items-center gap-2 text-neutral-400 text-[13px]">
                  <Timer className="w-5 h-5 text-[#00AEEF]" />
                  <span>Takes about 2 minutes</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-400 text-[13px]">
                  <BarChart3 className="w-5 h-5 text-[#00AEEF]" />
                  <span>Free personalized report</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-400 text-[13px]">
                  <Lock className="w-5 h-5 text-[#00AEEF]" />
                  <span>No spam, ever</span>
                </div>
              </div>
              <Button
                type="button"
                onClick={onOpen}
                className="bg-gradient-to-r from-[#00AEEF] to-[#2563eb] hover:from-[#00AEEF] hover:to-[#1d4ed8] text-white font-bold px-8 py-5 rounded-[10px] shadow-[0_4px_20px_rgba(0,174,239,0.3)] hover:shadow-[0_8px_30px_rgba(0,174,239,0.4)]"
              >
                Start Your Free Assessment{" "}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="hidden lg:block flex-1 min-w-[220px] relative"
            >
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-l from-[#00AEEF]/20 via-transparent to-transparent blur-2xl" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-[#00AEEF]/10 border border-[#00AEEF]/30 flex items-center justify-center mb-5">
                  <Lightbulb className="w-6 h-6 text-[#00AEEF]" />
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#00AEEF]/10 border border-[#00AEEF]/30 flex items-center justify-center opacity-80">
                    <Code className="w-5 h-5 text-[#00AEEF]" />
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-[#06B6D4]/10 border border-[#06B6D4]/30 flex items-center justify-center opacity-80">
                    <Sparkles className="w-5 h-5 text-[#06B6D4]" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RelatedSolutions({ page }) {
  const relatedServices = (page.relatedServicePaths || [])
    .map((p) => resolveServiceByPath(p))
    .filter(Boolean);
  const relatedLandings = getRelatedLandingPages(page.relatedLandingSlugs);
  const externalLinks = page.relatedExternalLinks || [];

  if (
    relatedServices.length === 0 &&
    relatedLandings.length === 0 &&
    externalLinks.length === 0
  ) {
    return null;
  }

  return (
    <section
      className="py-16 md:py-20 bg-[#0A0A0A] border-t border-white/5"
      data-testid="landing-related-solutions"
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-10"
        >
          <p className="text-[#00AEEF] text-xs uppercase tracking-widest font-medium mb-3">
            Related Solutions
          </p>
          <h3 className="text-3xl md:text-4xl font-semibold tracking-tight">
            Related work
          </h3>
        </motion.div>

        {relatedServices.length > 0 && (
          <div className="mb-10">
            <p className="text-gray-500 text-xs uppercase tracking-widest mb-4">
              Services
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedServices.map((service) => (
                <Link
                  key={service.path}
                  to={service.path}
                  className="group block bg-[#121212] border border-white/5 rounded-xl px-5 py-5 hover:border-[#00AEEF]/40 hover:bg-[#00AEEF]/5 transition-all"
                >
                  <div className="text-sm font-semibold text-white mb-1 group-hover:text-[#00AEEF] transition-colors">
                    {service.title}
                  </div>
                  <div className="text-gray-500 text-xs leading-relaxed line-clamp-2">
                    {service.metaDescription}
                  </div>
                  <span className="mt-3 inline-flex items-center gap-1 text-[11px] text-[#00AEEF] font-medium">
                    Learn more <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {relatedLandings.length > 0 && (
          <div className={externalLinks.length > 0 ? "mb-10" : undefined}>
            <p className="text-gray-500 text-xs uppercase tracking-widest mb-4">
              Related Landing Pages
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedLandings.map((rel) => (
                <Link
                  key={rel.slug}
                  to={rel.path}
                  className="group block bg-[#121212] border border-white/5 rounded-xl px-5 py-5 hover:border-[#00AEEF]/40 hover:bg-[#00AEEF]/5 transition-all"
                >
                  <div className="text-[11px] uppercase tracking-widest text-[#06B6D4] font-medium mb-2">
                    {landingCategoryMeta[rel.category]?.label || rel.category}
                  </div>
                  <div className="text-sm font-semibold text-white mb-1 group-hover:text-[#00AEEF] transition-colors">
                    {rel.title}
                  </div>
                  <div className="text-gray-500 text-xs leading-relaxed line-clamp-2">
                    {rel.metaDescription}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {externalLinks.length > 0 && (
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-widest mb-4">
              Also from the Maxiom Family
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {externalLinks.map((ext) => (
                <a
                  key={ext.url}
                  href={ext.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-[#121212] border border-white/5 rounded-xl px-5 py-5 hover:border-[#00AEEF]/40 hover:bg-[#00AEEF]/5 transition-all"
                  data-testid="landing-external-link"
                >
                  <div className="text-[11px] uppercase tracking-widest text-[#F59E0B] font-medium mb-2">
                    {ext.label}
                  </div>
                  <div className="text-sm font-semibold text-white mb-1 group-hover:text-[#00AEEF] transition-colors">
                    {ext.title}
                  </div>
                  <div className="text-gray-500 text-xs leading-relaxed line-clamp-2">
                    {ext.blurb}
                  </div>
                  <span className="mt-3 inline-flex items-center gap-1 text-[11px] text-[#00AEEF] font-medium">
                    Visit site <ArrowRight className="w-3 h-3" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function LandingShowcase({ page }) {
  const data = resolveLandingShowcase(page, getLandingShowcase(page.slug));
  if (!data || !data.visual) return null;
  const Visual = data.visual;
  const visualProps = data.visualProps || {};
  return (
    <section
      className="py-16 md:py-24 bg-[#0f0f0f] border-y border-white/5"
      data-testid="landing-showcase"
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {data.label && (
              <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-3">
                {data.label}
              </p>
            )}
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-5">
              {data.heading}
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-7">
              {data.description}
            </p>
            {data.bullets && data.bullets.length > 0 && (
              <ul className="space-y-3">
                {data.bullets.map((b, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3"
                    data-testid={`landing-showcase-bullet-${i}`}
                  >
                    <CheckCircle className="w-5 h-5 text-[#00AEEF] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm leading-relaxed">
                      {b}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Visual {...visualProps} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ProcessSnippet() {
  const steps = [
    { step: "01", title: "Discover", desc: "Name the constraint before we write code." },
    { step: "02", title: "Design", desc: "Enough UI to decide, not a 40-page deck." },
    { step: "03", title: "Develop", desc: "Senior engineers. Two-week slices you can see." },
    { step: "04", title: "Deploy", desc: "Ship, measure, change. We stay for the ugly part." },
  ];

  return (
    <section
      className="py-16 md:py-20 bg-[#0f0f0f] border-y border-white/5"
      data-testid="landing-process"
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-10"
        >
          <p className="text-[#00AEEF] text-xs uppercase tracking-widest font-medium mb-3">
            Our Process
          </p>
          <h3 className="text-3xl md:text-4xl font-semibold tracking-tight">
            How we build
          </h3>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <motion.div
              key={s.step}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={i}
              className="bg-[#121212] border border-white/5 rounded-xl p-6"
            >
              <div className="text-[#00AEEF] text-xs font-semibold mb-2">
                {s.step}
              </div>
              <div className="text-white font-medium mb-1">{s.title}</div>
              <div className="text-gray-500 text-sm">{s.desc}</div>
            </motion.div>
          ))}
        </div>
        <div className="mt-8">
          <Link
            to="/process"
            className="inline-flex items-center gap-2 text-[#00AEEF] text-sm font-medium hover:gap-3 transition-all"
          >
            See our full process <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage({ page }) {
  const location = useLocation();
  const [ideaValidatorOpen, setIdeaValidatorOpen] = useState(false);
  const [ideaValidatorFirstName, setIdeaValidatorFirstName] = useState("");
  const [costEstimatorOpen, setCostEstimatorOpen] = useState(false);
  const voice = useMemo(() => resolveLandingVoice(page), [page]);

  useFaqSchema(voice.faqs, page.path);
  useServiceSchema(page);
  useLocalBusinessSchema(page);

  const isCampaign = page.pageType === "campaign";
  const isCostEstimateCampaign = page.slug === "free-app-cost-estimate";
  const isIdeaAssessmentCampaign = page.slug === "free-app-idea-assessment";

  const canonicalUrl = `${SITE_ORIGIN}${page.path}`;
  const breadcrumbItems = useMemo(() => {
    const items = [{ name: "Home", url: SITE_ORIGIN }];
    const hub = HUB_BROWSE[page.category];
    if (hub) {
      items.push({ name: hub.label, url: hubUrlForCategory(page.category) });
    }
    items.push({ name: page.shortTitle || page.title, url: canonicalUrl });
    return items;
  }, [page, canonicalUrl]);

  useEffect(() => {
    if (window.gtag) {
      window.gtag("event", "landing_page_view", {
        page_path: location.pathname,
        landing_slug: page.slug,
        landing_type: page.pageType,
      });
    }
  }, [location.pathname, page.slug, page.pageType]);

  return (
    <>
      <Seo
        title={page.title}
        description={voice.metaDescription}
        ogDescription={voice.ogDescription}
        path={page.path}
        image={page.ogImage || page.coverImage}
        keywords={page.targetKeyword}
      />

      <BreadcrumbSchema items={breadcrumbItems} />

      {/* Visible breadcrumb trail (matches BreadcrumbList JSON-LD) */}
      <nav
        aria-label="Breadcrumb"
        className="bg-[#0A0A0A] border-b border-white/5 pt-24 pb-3"
        data-testid="landing-breadcrumb"
      >
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <ol className="flex flex-wrap items-center gap-1.5 text-[12px] text-gray-500">
            {breadcrumbItems.map((item, i) => {
              const isLast = i === breadcrumbItems.length - 1;
              return (
                <li key={item.url} className="flex items-center gap-1.5">
                  {i > 0 && (
                    <span className="text-gray-600" aria-hidden>
                      /
                    </span>
                  )}
                  {isLast ? (
                    <span className="text-gray-300" aria-current="page">
                      {item.name}
                    </span>
                  ) : (
                    <a
                      href={item.url}
                      className="hover:text-[#00AEEF] transition-colors"
                    >
                      {item.name}
                    </a>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </nav>

      {/* Hero with inline lead form */}
      <section
        className="pt-10 pb-16 md:pt-14 md:pb-20 bg-[#0A0A0A] relative overflow-hidden"
        data-testid={`landing-hero-${page.slug}`}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-[#00AEEF]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[#06B6D4]/5 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="lg:col-span-7"
            >
              {voice.heroEyebrow && (
                <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">
                  {voice.heroEyebrow}
                </p>
              )}
              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 whitespace-pre-line"
                data-testid="landing-heading"
              >
                {voice.heroHeadline}
              </h1>
              <p className="text-lg text-neutral-300 max-w-2xl leading-relaxed mb-8">
                {voice.heroSubheadline}
              </p>
              <TrustBullets items={voice.trustBullets} />
              {!isCampaign && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="#lead-form"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium px-8 py-3.5 text-base transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]"
                    data-testid="landing-hero-primary-cta"
                  >
                    {voice.primaryCtaLabel}
                    <ArrowRight className="w-5 h-5" />
                  </a>
                  <Link
                    to="/case-studies"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-white/20 hover:border-[#00AEEF] text-white hover:text-[#00AEEF] font-medium px-8 py-3.5 text-base transition-all duration-300"
                  >
                    View Case Studies <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              )}
            </motion.div>
            <div
              id="lead-form"
              className="lg:col-span-5 scroll-mt-24"
              data-testid={`landing-hero-form-${page.slug}`}
            >
              {isCostEstimateCampaign ? (
                <LandingLeadForm page={page} voice={voice} />
              ) : (
                <LandingLeadForm
                  page={page}
                  voice={voice}
                  onFirstNameChange={setIdeaValidatorFirstName}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <TrustBar />

      {/* Campaign pages keep things lean; SEO pages get the idea validator teaser */}
      {!isCampaign && (
        <IdeaValidatorTeaser onOpen={() => setIdeaValidatorOpen(true)} />
      )}

      {/* Offerings / Capabilities */}
      {page.offerings && page.offerings.length > 0 && (
        <section
          className="py-16 md:py-24 bg-[#0A0A0A]"
          data-testid="landing-offerings"
        >
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="mb-12"
            >
              <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-3">
                {isCampaign ? "What you get" : "What we build"}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                {isCampaign
                  ? "Inside the assessment"
                  : "The work, not the keyword"}
              </h2>
            </motion.div>
            <div
              className={`grid grid-cols-1 ${
                isCampaign
                  ? "sm:grid-cols-2 lg:grid-cols-4"
                  : "md:grid-cols-2 lg:grid-cols-3"
              } gap-5`}
            >
              {voice.offerings.map((f, i) => (
                <motion.div
                  key={f.title}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                  className="bg-[#121212] border border-white/5 rounded-xl p-6 hover:border-[#00AEEF]/30 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-lg bg-[#00AEEF]/10 flex items-center justify-center mb-5">
                    <f.icon className="w-6 h-6 text-[#00AEEF]" />
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2">
                    {f.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {f.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contextual showcase mockup paired with copy (per app-mockup-visuals rule) */}
      <LandingShowcase page={page} />

      {/* Long-form SEO content */}
      {page.longFormHeading && (
        <section
          className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/5"
          data-testid="landing-seo-content"
        >
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="mb-10 max-w-3xl"
            >
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                {voice.longFormHeading}
              </h2>
            </motion.div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <p className="text-gray-400 leading-relaxed text-base mb-5">
                  {voice.longFormBody1}
                </p>
                <p className="text-gray-400 leading-relaxed text-base">
                  {voice.longFormBody2}
                </p>
              </motion.div>
              {page.benefits && page.benefits.length > 0 && (
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={1}
                >
                  <ul className="space-y-4">
                    {voice.benefits.map((b, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#00AEEF] flex-shrink-0 mt-1" />
                        <div>
                          <div className="text-white font-medium mb-1">
                            {b.title}
                          </div>
                          <div className="text-gray-500 text-sm leading-relaxed">
                            {b.desc}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Industry / Location grid */}
      {page.industries && page.industries.length > 0 && (
        <section
          className="py-16 md:py-24 bg-[#0f0f0f] border-y border-white/5"
          data-testid="landing-industries"
        >
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="mb-10 max-w-3xl"
            >
              <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-3">
                {page.category === "location"
                  ? "Local Expertise"
                  : page.category === "industry"
                    ? "Sectors We Serve"
                    : "Industry Solutions"}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                {page.category === "location"
                  ? voice.isHqLocation
                    ? `Where we work in ${page.location?.region || "the DC Metro"}`
                    : `What ${page.location?.city || "this"} teams usually need`
                  : "Where this kind of app actually runs"}
              </h2>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {page.industries.map((ind, i) => (
                <motion.div
                  key={ind.name}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                  className="bg-[#121212] border border-white/5 rounded-xl p-6 hover:border-[#00AEEF]/20 transition-all"
                >
                  <h4 className="text-white font-medium mb-2">{ind.name}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {ind.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Process: SEO pages only */}
      {!isCampaign && <ProcessSnippet />}

      {/* Related solutions */}
      <RelatedSolutions page={page} />

      {/* FAQ */}
      {page.faqs && page.faqs.length > 0 && (
        <section
          className="py-16 md:py-24 bg-[#0A0A0A]"
          data-testid="landing-faq"
        >
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="lg:col-span-4"
              >
                <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-3">
                  FAQ
                </p>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-5">
                  Frequently Asked Questions
                </h2>
                <p className="text-gray-400 leading-relaxed">
                  If the FAQ doesn&apos;t cover it, schedule a call. A senior
                  engineer will walk the actual constraint, not a script.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-[#00AEEF] font-medium mt-5 hover:gap-3 transition-all"
                >
                  Get in Touch <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={1}
                className="lg:col-span-8"
              >
                <Accordion type="single" collapsible className="space-y-3">
                  {voice.faqs.map((faq, i) => (
                    <AccordionItem
                      key={i}
                      value={`landing-faq-${i}`}
                      className="bg-[#121212] border border-white/5 rounded-xl px-6 data-[state=open]:border-[#00AEEF]/30 transition-colors"
                    >
                      <AccordionTrigger className="text-white text-left font-medium py-5 hover:text-[#00AEEF] hover:no-underline [&[data-state=open]]:text-[#00AEEF]">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-400 text-sm leading-relaxed pb-5">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section
        className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/5"
        data-testid="landing-bottom-cta"
      >
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-5">
              {isCostEstimateCampaign
                ? "Want a senior engineer to sanity-check the range?"
                : isIdeaAssessmentCampaign
                  ? "Ready for a real read on the idea?"
                  : isCampaign
                    ? "If this is useful, take the next step."
                    : "If this is the work, schedule a call."}
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              {isCostEstimateCampaign
                ? "Six questions for an indicative range, or a call to turn it into a scoped plan. Not a binding quote."
                : isCampaign
                  ? isIdeaAssessmentCampaign
                    ? "Eight questions. A senior engineer reads what you send."
                    : "Take the next step with an engineer, not a BDR."
                  : "A senior engineer will look at what you sent. No deck theater."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {isCostEstimateCampaign ? (
                <button
                  type="button"
                  onClick={() => setCostEstimatorOpen(true)}
                  className="inline-flex w-full sm:w-auto min-h-[3.25rem] items-center justify-center gap-2 rounded-xl px-8 sm:px-10 py-3.5 text-base font-semibold tracking-tight text-white bg-gradient-to-r from-[#00AEEF] to-[#2563eb] shadow-[0_4px_20px_rgba(0,174,239,0.3)] transition-all duration-300 hover:from-[#00AEEF] hover:to-[#1d4ed8] hover:shadow-[0_8px_30px_rgba(0,174,239,0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00AEEF]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A]"
                >
                  Open the cost estimator
                  <ArrowRight className="w-5 h-5 shrink-0" aria-hidden />
                </button>
              ) : isCampaign ? (
                <button
                  type="button"
                  onClick={() => setIdeaValidatorOpen(true)}
                  className="inline-flex w-full sm:w-auto min-h-[3.25rem] items-center justify-center gap-2 rounded-xl px-8 sm:px-10 py-3.5 text-base font-semibold tracking-tight text-white bg-gradient-to-r from-[#00AEEF] to-[#2563eb] shadow-[0_4px_20px_rgba(0,174,239,0.3)] transition-all duration-300 hover:from-[#00AEEF] hover:to-[#1d4ed8] hover:shadow-[0_8px_30px_rgba(0,174,239,0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00AEEF]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A]"
                >
                  Start Your Free Assessment
                  <ArrowRight className="w-5 h-5 shrink-0" aria-hidden />
                </button>
              ) : (
                <a
                  href="#lead-form"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium px-8 py-3.5 text-base transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]"
                >
                  {voice.primaryCtaLabel}
                  <ArrowRight className="w-5 h-5" />
                </a>
              )}
              <Link
                to="/contact"
                className={
                  isCampaign
                    ? "inline-flex w-full sm:w-auto min-h-[3.25rem] items-center justify-center gap-2 rounded-xl px-8 sm:px-10 py-3.5 text-base font-semibold tracking-tight text-white border-2 border-white/20 hover:border-[#00AEEF] hover:text-[#00AEEF] transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00AEEF]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A]"
                    : "inline-flex items-center justify-center gap-2 rounded-md border border-white/20 hover:border-[#00AEEF] text-white hover:text-[#00AEEF] font-medium px-8 py-3.5 text-base transition-all duration-300"
                }
              >
                Schedule a Call <ArrowRight className="w-5 h-5 shrink-0" aria-hidden />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <IdeaValidatorModal
        open={ideaValidatorOpen}
        onOpenChange={setIdeaValidatorOpen}
        initialFirstName={ideaValidatorFirstName}
        initialStep={1}
        page={page}
      />
      {isCostEstimateCampaign ? (
        <AppCostEstimatorModal
          open={costEstimatorOpen}
          onOpenChange={setCostEstimatorOpen}
          page={page}
        />
      ) : null}
    </>
  );
}
